import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";

interface Star {
  id: number;
  x: number;
  y: number;
  size: number;
  baseOpacity: number;
  twinkleSpeed: number;
  twinkleDelay: number;
  color: "white" | "gold" | "warm" | "dark";
  layer: number;
}

interface Planet {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  glowColor: string;
  orbitRadius: number;
  orbitDuration: number;
  orbitDelay: number;
  direction: number;
}

interface Constellation {
  id: number;
  x: number;
  y: number;
  stars: { x: number; y: number }[];
  size: number;
}

function generateStars(count: number, layer: number, isLight: boolean): Star[] {
  return Array.from({ length: count }, (_, i) => {
    const colorPool: Star["color"][] = isLight
      ? (layer === 0
          ? ["dark", "dark", "dark", "gold"]
          : ["gold", "warm", "dark", "dark"])
      : (layer === 0
          ? ["white", "white", "white", "gold"]
          : ["gold", "warm", "white", "white"]);
    return {
      id: i + layer * 10000,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: layer === 0
        ? Math.random() * 1.2 + 0.3
        : Math.random() * 2 + 0.8,
      baseOpacity: layer === 0
        ? Math.random() * 0.3 + 0.2
        : Math.random() * 0.4 + 0.4,
      twinkleSpeed: Math.random() * 2 + 1.5,
      twinkleDelay: Math.random() * 8,
      color: colorPool[Math.floor(Math.random() * colorPool.length)],
      layer,
    };
  });
}

function generatePlanets(count: number, isLight: boolean): Planet[] {
  const planetConfigs = isLight
    ? [
        { color: "rgba(180, 160, 130, 0.6)", glowColor: "rgba(180, 160, 130, 0.2)", size: 6 },
        { color: "rgba(160, 140, 110, 0.5)", glowColor: "rgba(160, 140, 110, 0.15)", size: 4 },
        { color: "rgba(200, 180, 150, 0.7)", glowColor: "rgba(200, 180, 150, 0.25)", size: 8 },
        { color: "rgba(140, 120, 100, 0.4)", glowColor: "rgba(140, 120, 100, 0.12)", size: 5 },
      ]
    : [
        { color: "rgba(200, 170, 130, 0.8)", glowColor: "rgba(200, 170, 130, 0.3)", size: 6 },
        { color: "rgba(180, 150, 120, 0.7)", glowColor: "rgba(180, 150, 120, 0.25)", size: 4 },
        { color: "rgba(220, 190, 140, 0.9)", glowColor: "rgba(220, 190, 140, 0.35)", size: 8 },
        { color: "rgba(160, 140, 110, 0.6)", glowColor: "rgba(160, 140, 110, 0.2)", size: 5 },
      ];
  return Array.from({ length: count }, (_, i) => {
    const config = planetConfigs[i % planetConfigs.length];
    return {
      id: i,
      x: Math.random() * 60 + 20,
      y: Math.random() * 60 + 20,
      size: config.size,
      color: config.color,
      glowColor: config.glowColor,
      orbitRadius: Math.random() * 80 + 40,
      orbitDuration: Math.random() * 40 + 30,
      orbitDelay: Math.random() * 10,
      direction: Math.random() > 0.5 ? 1 : -1,
    };
  });
}

function generateConstellations(count: number): Constellation[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 80 + 10,
    y: Math.random() * 60 + 10,
    stars: Array.from({ length: 3 + Math.floor(Math.random() * 4) }, () => ({
      x: (Math.random() - 0.5) * 80,
      y: (Math.random() - 0.5) * 60,
    })),
    size: Math.random() * 0.8 + 0.5,
  }));
}

function StarField({ isLight }: { isLight: boolean }) {
  const starsFar = useMemo(() => generateStars(80, 0, isLight), [isLight]);
  const starsNear = useMemo(() => generateStars(40, 1, isLight), [isLight]);
  const planets = useMemo(() => generatePlanets(4, isLight), [isLight]);
  const constellations = useMemo(() => generateConstellations(3), []);

  const getStarColor = (color: Star["color"]) => {
    switch (color) {
      case "gold": return "rgba(120, 120, 120, 0.95)";
      case "warm": return "rgba(140, 140, 140, 0.85)";
      case "dark": return "rgba(60, 60, 60, 0.6)";
      default: return isLight ? "rgba(80, 70, 60, 0.5)" : "rgba(255, 255, 255, 0.95)";
    }
  };

  const getStarGlow = (color: Star["color"], size: number) => {
    switch (color) {
      case "gold": return `0 0 ${size * 3}px rgba(120, 120, 120, 0.4), 0 0 ${size * 6}px rgba(120, 120, 120, 0.15)`;
      case "warm": return `0 0 ${size * 3}px rgba(140, 140, 140, 0.3), 0 0 ${size * 6}px rgba(140, 140, 140, 0.1)`;
      case "dark": return `0 0 ${size * 3}px rgba(60, 60, 60, 0.15)`;
      default: return isLight
        ? `0 0 ${size * 3}px rgba(80, 70, 60, 0.2)`
        : `0 0 ${size * 3}px rgba(255, 255, 255, 0.4), 0 0 ${size * 6}px rgba(255, 255, 255, 0.15)`;
    }
  };

  const constellationColor = isLight ? "rgba(140, 140, 140, 0.2)" : "rgba(251, 191, 36, 0.15)";
  const constellationDotColor = isLight ? "rgba(140, 140, 140, 0.5)" : "rgba(251, 191, 36, 0.6)";

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Far layer - small twinkling stars */}
      {starsFar.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size,
            backgroundColor: getStarColor(star.color),
            boxShadow: getStarGlow(star.color, star.size),
          }}
          animate={{
            opacity: [star.baseOpacity * 0.3, star.baseOpacity, star.baseOpacity * 0.3],
            scale: [0.8, 1.1, 0.8],
          }}
          transition={{
            duration: star.twinkleSpeed,
            repeat: Infinity,
            delay: star.twinkleDelay,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Near layer - brighter twinkling stars */}
      {starsNear.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size,
            backgroundColor: getStarColor(star.color),
            boxShadow: getStarGlow(star.color, star.size),
          }}
          animate={{
            opacity: [star.baseOpacity * 0.2, star.baseOpacity, star.baseOpacity * 0.2],
            scale: [0.9, 1.2, 0.9],
          }}
          transition={{
            duration: star.twinkleSpeed * 0.7,
            repeat: Infinity,
            delay: star.twinkleDelay,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Constellation lines */}
      {constellations.map((constellation) => (
        <svg
          key={constellation.id}
          className="absolute pointer-events-none"
          style={{
            left: `${constellation.x}%`,
            top: `${constellation.y}%`,
            width: 200,
            height: 150,
            transform: `scale(${constellation.size})`,
            transformOrigin: "center",
          }}
        >
          {constellation.stars.map((star, idx) => {
            const nextStar = constellation.stars[(idx + 1) % constellation.stars.length];
            const cx1 = star.x + 100;
            const cy1 = star.y + 75;
            const cx2 = nextStar.x + 100;
            const cy2 = nextStar.y + 75;
            return (
              <motion.line
                key={idx}
                x1={cx1}
                y1={cy1}
                x2={cx2}
                y2={cy2}
                stroke={constellationColor}
                strokeWidth="0.8"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{
                  pathLength: [0, 1, 1, 0],
                  opacity: [0, 0.6, 0.6, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  delay: constellation.id * 2 + idx * 0.5,
                  ease: "easeInOut",
                }}
              />
            );
          })}
          {constellation.stars.map((star, idx) => (
            <motion.circle
              key={`dot-${idx}`}
              cx={star.x + 100}
              cy={star.y + 75}
              r={1.5}
              fill={constellationDotColor}
              animate={{
                opacity: [0.3, 0.9, 0.3],
                r: [1, 2, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: constellation.id * 2 + idx * 0.3,
                ease: "easeInOut",
              }}
            />
          ))}
        </svg>
      ))}

      {/* Planets - slowly orbiting spheres */}
      {planets.map((planet) => (
        <motion.div
          key={planet.id}
          className="absolute rounded-full"
          style={{
            width: planet.size,
            height: planet.size,
            background: `radial-gradient(circle at 30% 30%, ${planet.color}, ${planet.glowColor} 60%, transparent 100%)`,
            boxShadow: `0 0 ${planet.size * 2}px ${planet.glowColor}, 0 0 ${planet.size * 4}px ${planet.glowColor}`,
          }}
          animate={{
            x: [
              0,
              planet.orbitRadius * Math.cos(planet.orbitDelay) * planet.direction,
              0,
              planet.orbitRadius * Math.cos(planet.orbitDelay + Math.PI) * planet.direction,
              0,
            ],
            y: [
              0,
              planet.orbitRadius * Math.sin(planet.orbitDelay) * 0.4,
              planet.orbitRadius * 0.3,
              planet.orbitRadius * Math.sin(planet.orbitDelay + Math.PI) * 0.4,
              0,
            ],
            scale: [1, 0.85, 0.7, 0.85, 1],
            opacity: [0.6, 0.5, 0.35, 0.5, 0.6],
          }}
          transition={{
            duration: planet.orbitDuration,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}

      {/* Subtle ambient glow spots */}
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full"
        style={{
          left: "5%",
          top: "10%",
          background: isLight
            ? "radial-gradient(circle, rgba(180,180,180,0.06) 0%, transparent 60%)"
            : "radial-gradient(circle, rgba(251,191,36,0.04) 0%, transparent 60%)",
          filter: "blur(60px)",
        }}
        animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{
          right: "10%",
          bottom: "20%",
          background: isLight
            ? "radial-gradient(circle, rgba(160,160,160,0.04) 0%, transparent 60%)"
            : "radial-gradient(circle, rgba(180,160,140,0.03) 0%, transparent 60%)",
          filter: "blur(50px)",
        }}
        animate={{
          opacity: [0.2, 0.5, 0.2],
          scale: [1.1, 1, 1.1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </div>
  );
}

export default function Hero() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className={`absolute inset-0 ${isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"}`}>
        {isLight ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-[#f5f5f7]/40 via-transparent to-[#f5f5f7]" />
          </>
        ) : (
          <>
            {/* Dark theme: cosmic background */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
              style={{
                backgroundImage:
                  "url('/images/misc/hero_bg_1.jpg')",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-cosmic-950/60 via-cosmic-950/40 to-cosmic-950" />
          </>
        )}
      </div>

      {/* Animated Star Field */}
      {mounted && <StarField isLight={isLight} />}

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-24 pb-20">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-12 lg:gap-8">
          {/* Left - Title */}
          <div className="lg:max-w-[640px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wider mb-6 ${
                isLight
                  ? "bg-[#2d2d2d]/08 border border-[#2d2d2d]/15 text-[#2d2d2d]"
                  : "bg-gold-500/10 border border-gold-500/30 text-gold-400"
              }`}>
                {t("hero.badge")}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              {t("hero.title1")}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mt-2 ${
                isLight ? "text-[#1c1c1e]/30" : "text-white/40"
              }`}
            >
              {t("hero.title2")}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] mt-2 ${
                isLight ? "text-[#1c1c1e]/15" : "text-white/20"
              }`}
            >
              {t("hero.title3")}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className={`text-sm sm:text-base leading-[1.6] mt-5 ${
                isLight ? "text-[#2d2d2d]/70" : "text-gold-500/70"
              }`}
            >
              {t("hero.subtitle")}
            </motion.p>
          </div>

          {/* Right - Description + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="lg:w-[400px] flex flex-col gap-5"
          >
            <p className={`text-xl sm:text-2xl leading-relaxed font-semibold ${
              isLight ? "text-[#2d2d2d]" : "text-silver-400"
            }`}>
              {t("hero.desc_title")}
            </p>
            <p className={`text-sm sm:text-base leading-relaxed ${
              isLight ? "text-gray-500" : "text-silver-500"
            }`}>
              {t("hero.desc")}
            </p>
            <div className="pt-2">
              <Link
                to="/experience"
                className={`inline-flex items-center gap-3 rounded-full px-10 py-4 text-base sm:text-lg font-bold transition-all duration-200 whitespace-nowrap cursor-pointer group ${
                  isLight
                    ? "bg-gradient-to-r from-[#4a4a4c] to-[#5a5a5c] text-white hover:from-[#3a3a3c] hover:to-[#4a4a4c] shadow-lg shadow-[#4a4a4c]/15 hover:shadow-[#4a4a4c]/25"
                    : "bg-gradient-to-r from-gold-400 to-gold-600 text-cosmic-950 hover:from-gold-500 hover:to-gold-700 shadow-lg shadow-gold-500/20 hover:shadow-gold-500/40"
                }`}
              >
                {t("hero.cta")}
                <span className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                  isLight
                    ? "bg-white/20 group-hover:bg-white/30"
                    : "bg-cosmic-950/10 group-hover:bg-cosmic-950/20"
                }`}>
                  <i className={`ri-arrow-right-line text-sm ${isLight ? "text-white" : "text-cosmic-950"}`} />
                </span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className={`absolute bottom-0 left-0 right-0 h-32 pointer-events-none ${
        isLight
          ? "bg-gradient-to-t from-[#f5f5f7] to-transparent"
          : "bg-gradient-to-t from-cosmic-950 to-transparent"
      }`} />
    </section>
  );
}